<script setup lang="ts">
import {
  ref,
  useLocalePath,
  useSupabaseClient,
  onMounted,
  onUnmounted,
  watch,
} from '#imports';
import { useDebounceFn, onClickOutside } from '@vueuse/core';

const results = ref([]);
const searchTerm = ref('');
const isLoading = ref(false);
const showResults = ref(false);
const localePath = useLocalePath();
const supabase = useSupabaseClient();
const searchContainerRef = ref(null);

const props = defineProps({
  placeholder: {
    type: String,
    default: 'Search',
  },
});

onClickOutside(searchContainerRef, () => {
  showResults.value = false;
  searchTerm.value = ''
});

watch(
  searchTerm,
  useDebounceFn(() => {
    if (searchTerm.value && searchTerm.value !== '') {
      isLoading.value = true;
      ftsSearch();
    }
  }, 250),
);

const ftsSearch = async () => {
  const searchParams = {
    search_query: searchTerm.value,
    results_per_type: 40,
  };

  const { data } = await supabase.rpc('search_all', searchParams);

  results.value = data;
  showResults.value = true;
  isLoading.value = false;
};

// const router = useRouter()
const handleResultClick = (route: string) => {
  showResults.value = false;
  window.location.href = route;
};
</script>

<template>
  <div class="search-container" ref="searchContainerRef">
    <div class="search-input-wrapper" @click="showResults = true">
      <input
        v-model="searchTerm"
        class="search-input"
        :placeholder="props.placeholder"
      >
    </div>
    <div v-if="!isLoading && searchTerm && showResults && results && results.length > 0" class="search-results">
      <header area-label="Search results header">
        <h4>Top Results</h4>
        <small>{{ results.length }} results</small>
      </header>
      <div class="search-results-content">
        <div v-for="result in results.filter(result => result.type === 'map')" :key="result.id" class="search-result" @click="handleResultClick(localePath(`/maps/${result.id}`))">
          <MPImg :src="result.cover_image_url" /><span>{{ result.title }}<br><small>{{ result.type }}<span v-if="result.country_names[0]"> - {{ result.country_names[0] }}</span></small></span>
        </div>
        <div v-for="result in results.filter(result => result.type === 'profile')" :key="result.id" class="search-result" @click="handleResultClick(localePath(`/@${result.mappr_handle}`))">
          <MPImg :src="result.profile_image_url" /><span>{{ result.title }}<br><small>Creator</small></span>
        </div>
        <div v-for="result in results.filter(result => result.type === 'spot')" :key="result.id" class="search-result" @click="handleResultClick(localePath(`/maps/${result.foreign_id}/spots/${result.id}`))">
          <MPImg :src="result.cover_image_url" /><span>{{ result.title }}<br><small>{{ result.type }} <span v-if="result.city_names[0]"> - {{ result.city_names[0] }}</span></small></span>
        </div>
      </div>
    </div>

    <div v-if="!isLoading && searchTerm && showResults && results && results.length === 0" class="search-results">
      <p>{{ $t('search.noResults') }}</p>
    </div>
    <div v-if="isLoading" class="search-results">
      <p>Loading...</p>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/typography.pcss";
@import "@/assets/css/breakpoints.pcss";
@import "@/assets/css/shadows.pcss";

.search-container {
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
}

header {
  display: flex;
  align-items: center;
  padding: 1rem;
  /* border: 1px solid var(--color-slate-50); */
}

.search-results {
  position: absolute;
  top: 5rem;
  right:0 ;
  width: 100vw;
  background-color: white;
  z-index: 100;
  max-height: fit-content;
  margin-top: 0.5rem;
  scrollbar-width: none;
  border-radius: 0.5rem;
  box-shadow: var(--shadow-md);
  @mixin screen sm {
    top: 3.25rem;
    right: -5.25rem;
    width: 25rem;
    transform: translateX(-5rem);
    border-radius: 0.5rem;
  }
  h4 {
    font-size: 1.2rem;
    color: var(--color-slate-600);
    margin: 0;
  }
  p {
    margin: 1rem;
    text-align: center;
    color: var(--color-slate-400);
  }
  h4 + small {
    color: var(--color-slate-400);
    font-size: 0.8rem;
    margin: 0 1rem;
    line-height: 1.5;
    text-transform: capitalize;
    position: absolute;
    top: 1.15rem;
    right: 0;
  }
}

.search-results-content {
  overscroll-behavior: contain;
  overflow-y: scroll;
  overflow-x: hidden;
  height: calc(100vh - 10.5rem);
  @mixin screen sm {
    height: 30rem;
  }
}

.search-result {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  color: var(--color-slate-700);
  cursor: pointer;
  img {
    width: 2.5rem;
    height: 2.5rem;
    object-fit: cover;
    border-radius: 100%;
    margin-right: 0.5rem;
  }
  small {
    color: var(--color-slate-400);
    font-size: 0.8rem;
    text-transform: capitalize;
  }
  &:hover {
    background-color: var(--color-slate-50);
  }
}

.search-input-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: left;

  &:before {
    font-family: "remixicon";
    content: "\F0CD";
    position: absolute;
    color: var(--color-slate-400);
    left: 0.75rem;
    align-items: center;
  }
  input {
    padding: 0.5rem 1rem 0.5rem 2rem;
    border-radius: 1rem;
    font-size: 1rem;
    width: 100%;
    max-width: 28rem;
    border: 1px solid var(--color-slate-100);
    background: var(--color-slate-50);
    color: var(--color-slate-700);
    outline: none;
  }
  input::placeholder {
    font-size: 0.95rem;
    color: var(--color-slate-500);
  }
  input:focus {
    outline: none;
    border-color: var(--color-slate-200);
  }
}
</style>
